import { ContactsData, IVacancyCvSearchParams } from 'data/interfaces/Common';
import { ICV, ICVCreate } from 'data/interfaces/ICV';
import { ProfileType } from 'data/interfaces/IRegistration';
import CvRepository from 'data/repositories/CvRepository';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { Dispatch, SetStateAction, createContext, useContext, useState } from 'react';
import { CookiesType } from 'types/enums';
import { AppMetricaEvent, sendToMetrika } from 'utils/sendToMetrics';
import { useUserContext } from './user_state';

interface IState {
  savedCvs: ICV[];
  foundCvs: Map<number, ICV>;
  getCv: (id: number, clear?: boolean) => Promise<boolean>;
  openContacts: (id: number) => Promise<void>;
  currentCv: ICV;
  setSavedCvs: () => Promise<boolean>;
  saveCv: (id: number) => Promise<boolean>;
  removeCvfromSaved: (id: number) => Promise<boolean>;
  deleteCv: (id: number) => void;
  updateCv: (
    id: number,
    cv: Partial<ICV>,
    onSave?: () => void,
    onError?: (err: Error) => void,
  ) => void;
  editCv: (cv: ICV) => void;
  findCvs: (data?: IVacancyCvSearchParams, _page?: number) => Promise<boolean>;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  total: number;
  setBlackListedCVs: () => void;
  removeFromBlackList: (id?: number) => void;
  moveToTop: (cvId: number, onSet?: () => void) => void;
  setFoundCvsState: Dispatch<SetStateAction<Map<number, ICV>>>;
}

const CvContext = createContext<Partial<IState>>({});

interface Props {
  children: React.ReactNode;
  cv?: ICV;
}

export function CvWrapper(props: Props) {
  const router = useRouter();
  const { currentProfile } = useUserContext();
  const [foundCvs, setFoundCvsState] = useState<Map<number, ICV>>(new Map());
  const [savedCvs, setSavedCvsState] = useState<ICV[]>([]);
  const [currentCv, setCurrentCvState] = useState<ICV>(props.cv || null);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(undefined);

  const setSavedCvs = async (): Promise<boolean> => {
    if (currentProfile?.profileType === ProfileType.Hirer) {
      return CvRepository.getSavedByCurrentUser()
        .then((res) => {
          res = res.map((el) => {
            return { ...el, isSavedByCurrentProfile: true };
          });
          setSavedCvsState(res);
          return true;
        })
        .catch(() => {
          return false;
        });
    } else if (currentProfile?.profileType === ProfileType.Employee) {
      return CvRepository.getCVListOfCurrentUser(Cookies.get(CookiesType.accessToken))
        .then((res) => {
          setSavedCvsState(res);
          return true;
        })
        .catch(() => {
          return false;
        });
    }
  };

  const saveCv = async () => {
    try {
      sendToMetrika(AppMetricaEvent.AddToSaved);
      setSavedCvs();
      return true;
    } catch (e) {
      return false;
    }
  };

  const removeCvfromSaved = async (id: number) => {
    try {
      await CvRepository.removeCvFromSaved(id);
      setSavedCvsState((state) => state.filter((el) => el.id !== id));
      return true;
    } catch (e) {
      return false;
    }
  };

  const deleteCv = (id: number) => {
    CvRepository.deleteCV(id).then(() => {
      setSavedCvsState((state) => state.filter((el) => el.id !== id));
    });
  };

  const updateCv = (
    id: number,
    cv: Partial<ICV>,
    onSave?: () => void,
    onError?: (err: Error) => void,
  ) => {
    const cvCp: Partial<ICV> = JSON.parse(JSON.stringify(cv));

    const contacts: Omit<ContactsData, 'expiredAt'> = {
      email: cvCp.email,
      phone: cvCp.phone,
      telegram: cvCp.telegramNickname,
    };

    delete cvCp.phone;
    delete cvCp.email;
    delete cvCp.telegramNickname;

    const editContactPromise = CvRepository.editContacts(
      id,
      Cookies.get(CookiesType.profileType) as ProfileType,
      Cookies.get(CookiesType.accessToken),
      contacts,
    );
    const updateCvPromise = CvRepository.updateCV(id, cvCp as ICVCreate);

    Promise.all([editContactPromise, updateCvPromise])
      .then(() => {
        onSave ? onSave() : setSavedCvs();
      })
      .catch(onError);
  };

  const findCvs = async (data?: IVacancyCvSearchParams, _page?: number) => {
    const res = await CvRepository.findCvs(data, _page || page, 10);
    if (typeof res === 'object') {
      setFoundCvsState((state) => {
        if ((_page || page) === 1) state.clear();
        res.data.forEach((el) => state.set(el.id, el));
        return new Map(state);
      });
      setTotal(res.total);
    }
    return true;
  };

  const editCv = (cv: ICV) => {
    router.push(`/create/cv?id=${cv.id}`);
  };

  const getCv = (id: number, clear?: boolean): Promise<boolean> => {
    if (clear) {
      setCurrentCvState(undefined);
      return new Promise((res) => res(true));
    } else {
      return CvRepository.getById(id)
        .then((res) => {
          setCurrentCvState(res);
          return true;
        })
        .catch(() => {
          setCurrentCvState(undefined);
          return false;
        });
    }
  };

  const openContacts = async (): Promise<void> => {
    return setCurrentCvState((cv) => ({ ...cv, isContactsOpen: true }));
  };

  const moveToTop = (cvId: number, onSet?: () => void) => {
    CvRepository.moveToTop(cvId).then(() => {
      onSet ? onSet() : setSavedCvs();
    });
  };

  const setBlackListedCVs = () => {
    CvRepository.getBlackListedByCurrentProfile().then((res) => {
      setSavedCvsState(res.data);
    });
  };

  const removeFromBlackList = (id?: number) => {
    CvRepository.removeFromBlackList(id).then(() => {
      setSavedCvsState((state) => state.filter((el) => el.id !== id));
    });
  };

  const value: IState = {
    savedCvs,
    foundCvs,
    setSavedCvs,
    saveCv,
    removeCvfromSaved,
    deleteCv,
    updateCv,
    editCv,
    findCvs,
    getCv,
    openContacts,
    currentCv,
    page,
    setPage,
    total,
    setBlackListedCVs,
    removeFromBlackList,
    moveToTop,
    setFoundCvsState,
  };

  return <CvContext.Provider value={value}>{props.children}</CvContext.Provider>;
}

export function useCvContext() {
  return useContext(CvContext);
}
